import '../../../assets/styles/tools/imageRemoveBg.css';
import '../../../assets/styles/App.css';
import React, {MouseEvent, useEffect, useRef, useState, WheelEvent} from "react";
import {useImageEditorInfoStore} from "../../../store/imageEditorInfoStore";
import {CubeGridLoader} from "../../loading/CubeGridLoader";
import {ColorPicker, Segmented, Tooltip} from "antd";
import {Layers2, Plus, Trash2, Upload, X, ZoomIn, ZoomOut} from 'lucide-react';
import {imageBgRemove, modelIsLoaded} from "./imageBgRemove";
import type {AggregationColor} from "antd/es/color-picker/color";
import html2canvas from "html2canvas";
import {basicDownloadImage} from "../../../utils";
import {DemoBgImageList, ModelTypeConstant} from "../../../constants";
import {modelExists} from "../../../utils/model";
import {useAlertMessage} from "../../../hooks/AlertContext";

type IImageEditorProps = {
    closeEditorTable?: () => void;
}

const maxWidthSize = 900;// 图片宽最大为900px
const maxHeightSize = 600;// 图片高最大为600px
const minWidthSize  = 300;// 图片宽最小为300px
const minHeightSize = 300; // 图片高最小为 300px

const minScale= 1;// 图片最小放缩倍率
const maxScale = 10;// 图片最大放缩倍率
const zoomIntensity = 0.1;// 图片缩放速度

// 编辑背景色的基本数据
const editToolBgTypes = ['Color', 'Photo'];
const bgColorList = [
    'rgb(255, 255, 255)', 'rgb(0, 0, 0)', 'rgb(204, 204, 204)', 'rgb(210, 92, 74)', 'rgb(198, 71, 102)', 'rgb(134, 62, 165)',
    'rgb(92, 68, 170)', 'rgb(69, 85, 169)', 'rgb(91, 149, 229)', 'rgb(99, 167, 232)', 'rgb(111, 184, 206)', 'rgb(87, 146, 136)',
    'rgb(119, 170, 101)', 'rgb(167, 199, 120)', 'rgb(249, 234, 118)', 'rgb(238, 194, 91)', 'rgb(229, 159, 78)', 'rgb(221, 106, 69)',
    'rgb(158, 133, 99)', 'rgb(151, 149, 149)', 'rgb(105, 123, 134)'
];
const bgLinearGradientList = [
    'linear-gradient(-225deg, rgb(255, 198, 132) 0%, rgb(251, 150, 128) 100%)', 'linear-gradient(-225deg, rgb(214, 255, 195) 0%, rgb(255, 198, 132) 100%)',
    'linear-gradient(-225deg, rgb(230, 246, 197) 0%, rgb(77, 175, 154) 100%)', 'linear-gradient(-225deg, rgb(179, 242, 203) 0%, rgb(81, 205, 200) 100%)',
    'linear-gradient(-225deg, rgb(161, 241, 241) 0%, rgb(133, 193, 238) 100%)', 'linear-gradient(-225deg, rgb(119, 205, 242) 0%, rgb(42, 124, 224) 100%)',
    'linear-gradient(-225deg, rgb(170, 220, 249) 0%, rgb(185, 139, 221) 100%)', 'linear-gradient(-225deg, rgb(180, 161, 246) 0%, rgb(204, 134, 224) 100%)',
    'linear-gradient(-225deg, rgb(239, 149, 218) 0%, rgb(249, 130, 142) 100%)', 'linear-gradient(-180deg, rgb(209, 212, 213) 0%, rgb(250, 250, 250) 100%)',
    'linear-gradient(-180deg, rgb(250, 236, 196) 0%, rgb(250, 250, 250) 100%)', 'linear-gradient(-180deg, rgb(219, 237, 210) 0%, rgb(250, 250, 250) 100%)',
    'linear-gradient(-180deg, rgb(204, 230, 222) 0%, rgb(250, 250, 250) 100%)', 'linear-gradient(-180deg, rgb(211, 235, 251) 0%, rgb(250, 250, 250) 100%)',
    'linear-gradient(-180deg, rgb(201, 217, 244) 0%, rgb(250, 250, 250) 100%)', 'linear-gradient(-180deg, rgb(210, 199, 211) 0%, rgb(250, 250, 250) 100%)',
    'linear-gradient(-180deg, rgb(229, 203, 212) 0%, rgb(250, 250, 250) 100%)', 'linear-gradient(-180deg, rgb(233, 192, 193) 0%, rgb(250, 250, 250) 100%)',
    'linear-gradient(-180deg, rgb(243, 255, 190) 0%, rgb(248, 249, 247) 100%)'
]
export const ImageEditor: React.FC<IImageEditorProps> = ({
    closeEditorTable = () => {}
}) => {
    const {showAlert} = useAlertMessage();

    const {finalCanvasBase64, originalCanvasBase64, setFinalCanvasBase64, setOriginalCanvasBase64} = useImageEditorInfoStore();

    const canvasContainerRef = React.createRef<HTMLDivElement>();

    const finalCanvasRef = React.createRef<HTMLImageElement>();
    const originalCanvasRef = React.createRef<HTMLImageElement>();

    const [isLoading, setIsLoading] = useState(true);
    const [editToolBgType, setEditToolBgType] = useState(editToolBgTypes[0]);// 编辑工具标签页的页下标

    const [scale, setScale] = useState(1);// 当前图片的缩放率
    const [transformOrigin, setTransformOrigin] = useState('50% 50%');// 缩放基点
    const [translateX, setTranslateX] = useState(0);// 图片X轴平移
    const [translateY, setTranslateY] = useState(0);// 图片Y轴平移
    const dragState = useRef({ isDragging: false, startX: 0, startY: 0 }); // 拖动状态

    const [isShowOriginalImg, setIsShowOriginalImg] = useState(false);// 是否显示原始图像
    const [layers2Color, setLayers2Color] = useState('black');// layers2图标的颜色（开启原始图像就显示其他颜色，关闭则是默认黑色）

    const fileInputRef = React.createRef<HTMLInputElement>();// 文件上传ref
    const bgImgFileRef = React.createRef<HTMLInputElement>();// 图片文件上传ref

    const [curUploadBgImgFile, setCurUploadBgImgFile] = useState('');// 当前上传作为图片背景的图片文件
    const [isUseCurUploadBgImg, setIsUseCurUploadBgImg] = useState(false);// 是否使用当前上传为图片文件作为背景

    const [showImageStyle, setShowImageStyle] = useState({
        backgroundColor: '#fff',
        backgroundImage:
            'linear-gradient(45deg, rgb(240, 240, 240) 25%, transparent 0px),' +
            'linear-gradient(45deg, transparent 75%, rgb(240, 240, 240) 0px),' +
            'linear-gradient(45deg, rgb(240, 240, 240) 25%, transparent 0px),' +
            'linear-gradient(45deg, transparent 75%, rgb(240, 240, 240) 0px)',
        backgroundPosition: '0px 0px, 10px 10px, 10px 10px, 20px 20px',
        backgroundClip: 'content-box',
        backgroundSize: '20px 20px',
    });

    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    useEffect(() => {// 监听窗口大小变化
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getReasonableSize = (width: number, height: number) => {
        const aspectRatio = width / height; // 原始宽高比

        let newWidth = width;
        let newHeight = height;

        // 根据窗口大小调整图片宽度
        if (windowSize.width < maxWidthSize) {
            newWidth = windowSize.width * 0.5; // 图片宽度为窗口宽度的 50%
            newHeight = newWidth / aspectRatio; // 保持宽高比
        }

        // 如果调整后的尺寸超出最大限制，按最大限制调整
        if (newWidth > maxWidthSize || newHeight > maxHeightSize) {
            if (newWidth > maxWidthSize) {
                newWidth = maxWidthSize;
                newHeight = newWidth / aspectRatio;
            }
            if (newHeight > maxHeightSize) {
                newHeight = maxHeightSize;
                newWidth = newHeight * aspectRatio;
            }
        }

        // 如果调整后的尺寸小于最小限制，按最小限制调整
        if (newWidth < minWidthSize || newHeight < minHeightSize) {
            if (newWidth < minWidthSize) {
                newWidth = minWidthSize;
                newHeight = newWidth / aspectRatio;
            }
            if (newHeight < minHeightSize) {
                newHeight = minHeightSize;
                newWidth = newHeight * aspectRatio;
            }
        }

        return { newWidth, newHeight };
    }

    useEffect(() => {
        const container = canvasContainerRef.current;
        if (finalCanvasBase64 && container && finalCanvasRef.current) {
            const img = new Image();
            img.src = finalCanvasBase64;
            img.onload = () => {
                const {newWidth, newHeight} = getReasonableSize(img.width, img.height);
                container.style.minWidth = `min(${newWidth}px, 100%)`;
                container.style.maxWidth = '100%';
                container.style.width = newWidth + 'px';
                container.style.minHeight = `min(80%, ${newHeight}px)`;
                container.style.maxHeight = '80%';
                container.style.height = newHeight + 'px';
            };
        }
    }, [finalCanvasBase64]);

    useEffect(() => {
        const container = canvasContainerRef.current;
        if (originalCanvasBase64 && container && originalCanvasRef.current) {
            const img = new Image();
            img.src = originalCanvasBase64;
            img.onload = () => {
                const {newWidth, newHeight} = getReasonableSize(img.width, img.height);
                container.style.minWidth = `min(${newWidth}px, 100%)`;
                container.style.maxWidth = '100%';
                container.style.width = newWidth + 'px';
                container.style.minHeight = `min(80%, ${newHeight}px)`;
                container.style.maxHeight = '80%';
                container.style.height = newHeight + 'px';
            };
        }
    }, [originalCanvasBase64, windowSize]);

    useEffect(() => {
        setIsLoading(false);
    }, [finalCanvasRef.current]);

    const setIsShowOriginalImgState = () => {// 设置是否显示原始图像
        setLayers2Color(isShowOriginalImg ? 'black' : '#C56D00');
        setIsShowOriginalImg(!isShowOriginalImg);
    }

    const uploadFile = async (ev: any) => {// 重新上传文件
        setIsLoading(false);//重置
        if (!modelIsLoaded()) {
            showAlert({
                message: "Loading model and trying again later",
                type: "warning",
            })
            return;
        }

        const file = ev.currentTarget.files?.[0];
        if (file) {
            setIsLoading(true);

            const imageUrl = URL.createObjectURL(file);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                // 图像加载完成，处理图像数据
                imageBgRemove(image, showAlert).then((res: any) => {
                    if (!res || !res.resultImgBase64) return;
                    // @ts-ignore
                    setFinalCanvasBase64(res.resultImgBase64);
                    setOriginalCanvasBase64(res.originImgBase64);
                    setIsLoading(false);
                });
            };
        }
    }

    const handleUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';// 重置文件输入值
            fileInputRef.current.click();
        }
    }

    const setShowImageBgTransparent = () => {// 设置透明背景
        setIsUseCurUploadBgImg(false);
        setShowImageStyle({
            backgroundColor: '#fff',
            backgroundImage:
                'linear-gradient(45deg, rgb(240, 240, 240) 25%, transparent 0px),' +
                'linear-gradient(45deg, transparent 75%, rgb(240, 240, 240) 0px),' +
                'linear-gradient(45deg, rgb(240, 240, 240) 25%, transparent 0px),' +
                'linear-gradient(45deg, transparent 75%, rgb(240, 240, 240) 0px)',
            backgroundPosition: '0px 0px, 10px 10px, 10px 10px, 20px 20px',
            backgroundClip: 'content-box',
            backgroundSize: '20px 20px',
        });
    }

    const updateBgColorByColorPicker = (color: AggregationColor) => {
        if (color.getColors().length === 1)
            setNormalBgColor(color.toCssString());
        else
            setLinearGradientBgColor(color.toCssString());
    }

    const setNormalBgColor = (color: string) => {// 设置常规的背景颜色
        setIsUseCurUploadBgImg(false);
        setShowImageStyle({
            backgroundColor: color,
            backgroundImage: '',
            backgroundPosition: '',
            backgroundClip: '',
            backgroundSize: '',
        });
    }

    const setLinearGradientBgColor = (color: string) => {// 设置渐变背景颜色
        setIsUseCurUploadBgImg(false);
        setShowImageStyle({
            backgroundColor: '',
            backgroundImage: color,
            backgroundPosition: '',
            backgroundClip: '',
            backgroundSize: 'cover',
        });
    }

    const uploadImgFile = (ev: any) => {// 上传图片文件
        const file = ev.currentTarget.files?.[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setCurUploadBgImgFile(imageUrl);
            setIsUseCurUploadBgImg(true);
            setShowImageStyle({
                backgroundColor: '',
                backgroundImage: `url('${imageUrl}')`,
                backgroundPosition: '',
                backgroundClip: '',
                backgroundSize: 'cover',
            });
        }
    }
    const handleUploadImgFile = () => {
        if (bgImgFileRef.current) {
            bgImgFileRef.current.value = '';// 重置文件输入值
            bgImgFileRef.current.click();
        }
    }

    const setImgBg2Img = (img: string) => {
        setIsUseCurUploadBgImg(false);
        setShowImageStyle({
            backgroundColor: '',
            backgroundImage: `url('${img}')`,
            backgroundPosition: '',
            backgroundClip: '',
            backgroundSize: 'cover',
        });
    }

    const zoomInImage = () => {
        const newScale = scale + zoomIntensity;
        if (newScale < minScale || newScale > maxScale) return;
        setScale(newScale);
    }

    const zoomOutImage = () => {
        const newScale = scale - zoomIntensity;
        if (newScale < minScale || newScale > maxScale) return;
        setScale(newScale);

        // 重置平移当缩放到等于 1
        if (newScale === 1) {
            setTranslateX(0);
            setTranslateY(0);
            setTransformOrigin('50% 50%');
        }
    }

    const zoomImage = (e: WheelEvent) => {
        // 计算缩放比例
        const delta = e.deltaY;
        const newScale = delta > 0 ? scale - zoomIntensity : scale + zoomIntensity;

        if (newScale < minScale || newScale > maxScale) return;

        // 计算鼠标相对位置
        const rect = canvasContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        setTransformOrigin(`${mouseX}px ${mouseY}px`);
        setScale(newScale);

        if (newScale === 1) {
            // 重置平移当缩放到等于 1
            setTranslateX(0);
            setTranslateY(0);
            setTransformOrigin('50% 50%');// 重置缩放基点

            // 触发一次拖动事件
            const startX = mouseX, startY = mouseY;
            const endX = mouseX + 50, endY = mouseY + 50;
            if (canvasContainerRef.current) {
                // 触发拖拽开始
                const dragStartEvent = new DragEvent('dragstart', { bubbles: true });
                dragStartEvent.dataTransfer?.setData('text/plain', 'item-id');
                canvasContainerRef.current.dispatchEvent(dragStartEvent);

                // 触发拖拽移动
                const dragEvent = new DragEvent('drag', {
                    clientX: startX,
                    clientY: startY
                });
                canvasContainerRef.current.dispatchEvent(dragEvent);

                // 触发拖拽结束
                const dragEndEvent = new DragEvent('dragend', {
                    clientX: endX,
                    clientY: endY
                });
                canvasContainerRef.current.dispatchEvent(dragEndEvent);
            }
        }
    }

    // 鼠标按下
    const handleMouseDown = (event: MouseEvent) => {
        if (scale <= 1) return; // 仅当放大后才允许拖动

        event.preventDefault();
        dragState.current = {
            isDragging: true,
            startX: event.clientX - translateX,
            startY: event.clientY - translateY,
        };
    };

    // 鼠标移动
    const handleMouseMove = (event: MouseEvent) => {
        if (!dragState.current.isDragging) return;

        let newTranslateX = event.clientX - dragState.current.startX;
        let newTranslateY = event.clientY - dragState.current.startY;

        const img = isShowOriginalImg ? originalCanvasRef.current : finalCanvasRef.current;
        if (img) {
            const rect = img.getBoundingClientRect();
            const maxTranslateX = (rect.width * scale - rect.width) / 2;
            const maxTranslateY = (rect.height * scale - rect.height) / 2;

            newTranslateX = Math.min(Math.max(newTranslateX, -maxTranslateX), maxTranslateX);
            newTranslateY = Math.min(Math.max(newTranslateY, -maxTranslateY), maxTranslateY);
            setTranslateX(
                newTranslateX
            );
            setTranslateY(
                newTranslateY
            );
        }
    };

    // 鼠标松开
    const handleMouseUp = () => {
        dragState.current.isDragging = false;
    };

    // 鼠标离开容器时停止拖动
    const handleMouseLeave = () => {
        dragState.current.isDragging = false;
    };


    const downloadImage = async () => {
        if (!finalCanvasRef.current) return;
        const finalCanvas = finalCanvasRef.current;
        try {
            const tempImg = new Image();
            tempImg.crossOrigin = "anonymous";
            tempImg.src = finalCanvasBase64;
            if (finalCanvas.style.backgroundClip === 'content-box')// 带有透明背景属性的img
                tempImg.style.backgroundColor = '';// 设置空背景
            else {
                tempImg.style.backgroundColor = finalCanvas.style.backgroundColor;
                tempImg.style.backgroundImage = finalCanvas.style.backgroundImage;
                tempImg.style.backgroundSize = finalCanvas.style.backgroundSize;
            }
            tempImg.style.position = 'fixed';
            tempImg.style.top = '-99999999px';
            document.body.appendChild(tempImg);
            let canvas = await html2canvas(tempImg, {
                useCORS: true,
                logging: true,
                backgroundColor: 'transparent',
                scale: 3,
            });
            basicDownloadImage(canvas.toDataURL('image/png', 10), `IMG_${Date.now()}.png`);

            tempImg.remove();
        } catch (error) {
            console.error('Image Download Error:', error);
        }
    };

    return (
        <div className="i-editor-container" onContextMenu={(e) => e.preventDefault()}>
            <div className="i-editor-contain">
                <div className="i-left-container">
                    <div className="i-editor">
                        <div
                            // @ts-ignore
                            ref={canvasContainerRef}
                            className="canvas-container cursor-grab active:cursor-grabbing"
                            onWheel={zoomImage}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                            onDragStart={(e) => e.preventDefault()}
                        >
                            <CubeGridLoader
                                dotBackgroundColor="#3498db"
                                isOpen={isLoading}
                            />
                            {/*前景图像*/}
                            {!isShowOriginalImg &&
                                <img
                                    // @ts-ignore
                                    ref={finalCanvasRef}
                                    src={finalCanvasBase64}
                                    style={{
                                        backgroundColor: showImageStyle.backgroundColor,
                                        backgroundImage: showImageStyle.backgroundImage,
                                        backgroundPosition: showImageStyle.backgroundPosition,
                                        backgroundClip: showImageStyle.backgroundClip,
                                        backgroundSize: showImageStyle.backgroundSize,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                                        transition: dragState.current.isDragging ? 'none' : 'transform 0.1s ease-out',
                                        transformOrigin: transformOrigin
                                    }}
                                />
                            }
                            {/*原始图像*/}
                            {isShowOriginalImg &&
                                <img
                                    // @ts-ignore
                                    ref={originalCanvasRef}
                                    src={originalCanvasBase64}
                                    style={{
                                        backgroundColor: showImageStyle.backgroundColor,
                                        backgroundImage: showImageStyle.backgroundImage,
                                        backgroundPosition: showImageStyle.backgroundPosition,
                                        backgroundClip: showImageStyle.backgroundClip,
                                        backgroundSize: showImageStyle.backgroundSize,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                                        transition: dragState.current.isDragging ? 'none' : 'transform 0.1s ease-out',
                                        transformOrigin: transformOrigin
                                    }}
                                />
                            }
                            {/* 涂鸦画布 */}
                            {/*<canvas*/}
                            {/*    ref={canvasRef}*/}
                            {/*    style={{ position: 'absolute', top: 0, left: 0 }}*/}
                            {/*    onMouseDown={handleMouseDown}*/}
                            {/*    onMouseMove={handleMouseMove}*/}
                            {/*    onMouseUp={handleMouseUp}*/}
                            {/*    onMouseLeave={handleMouseUp}*/}
                            {/*/>*/}
                        </div>
                        <div className="i-bottom-opt-button">
                            <div></div>
                            <div className="flex gap-[5%]">
                                <Tooltip placement="bottom" title={isShowOriginalImg ? 'Processed img' : 'Original img'}>
                                    <button onClick={setIsShowOriginalImgState}>
                                        <Layers2 style={{width: '20px', height: '18px', color: layers2Color}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Zoom in">
                                    <button onClick={zoomInImage}>
                                        <ZoomIn style={{width: '20px', height: '18px'}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Zoom out">
                                    <button onClick={zoomOutImage}>
                                        <ZoomOut style={{width: '20px', height: '18px'}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Reupload">
                                    <button onClick={handleUpload}>
                                        <Upload style={{width: '20px', height: '18px'}}/>
                                        <input
                                            // @ts-ignore
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={uploadFile}
                                            accept="image/png, image/jpeg, image/webp"
                                        />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="i-right-container">
                    <Segmented
                        options={editToolBgTypes}
                        onChange={(e) => setEditToolBgType(e)}
                        block
                    />
                    {editToolBgType === editToolBgTypes[0] &&
                        <div className="i-landing-tool-info-background__panel">
                            <div className="i-landing-tool-content" onClick={setShowImageBgTransparent}>
                                <div className="i-landing-tool__content">
                                    <div
                                        className="
                                            i-landing-tool-info-background__item-content
                                            i-landing-tool-info-background__item-content--transparent
                                        "
                                    ></div>
                                </div>
                            </div>
                            <div className="i-landing-tool-content">
                                <div className="i-landing-tool__content">
                                    <div
                                        className="i-landing-tool-info-background__item-content"
                                        style={{
                                            backgroundImage: `url('/images/my_tools/editor/blur_bg.webp')`,
                                            backgroundSize: 'cover'
                                        }}
                                    ></div>
                                </div>
                            </div>
                            <div className="i-landing-tool-content">
                                <div className="i-landing-tool__content">
                                    <ColorPicker
                                        allowClear
                                        showText
                                        mode={['single', 'gradient']}
                                        className="hidden"
                                        onChange={updateBgColorByColorPicker}
                                    >
                                        <div
                                            className="i-landing-tool-info-background__item-content"
                                            style={{
                                                backgroundImage: `url('/images/my_tools/editor/color_selector.png')`,
                                                backgroundSize: 'cover'
                                            }}
                                        >
                                        </div>
                                    </ColorPicker>
                                </div>
                            </div>
                            {bgColorList.map((color) => (
                                <div className="i-landing-tool-content" onClick={() => setNormalBgColor(color)}>
                                    <div className="i-landing-tool__content">
                                        <div
                                            className="i-landing-tool-info-background__item-content"
                                            style={{backgroundColor: color}}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                            {bgLinearGradientList.map((color) => (
                                <div className="i-landing-tool-content" onClick={() => setLinearGradientBgColor(color)}>
                                    <div className="i-landing-tool__content">
                                        <div
                                            className="i-landing-tool-info-background__item-content"
                                            style={{backgroundImage: color, backgroundSize: 'cover'}}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                    {editToolBgType === editToolBgTypes[1] &&
                        <div className="i-landing-tool-info-background__panel">
                            <div className="i-landing-tool-content" onClick={setShowImageBgTransparent}>
                                <div className="i-landing-tool__content">
                                    <div
                                        className="
                                            i-landing-tool-info-background__item-content
                                            i-landing-tool-info-background__item-content--transparent
                                        "
                                    ></div>
                                </div>
                            </div>
                            <div className="i-landing-tool-content" onClick={() => {
                                if (!curUploadBgImgFile)
                                    handleUploadImgFile();
                                else {
                                    if (isUseCurUploadBgImg) {
                                        setCurUploadBgImgFile('');
                                        setShowImageBgTransparent()
                                    }else {
                                        setImgBg2Img(curUploadBgImgFile);
                                        setIsUseCurUploadBgImg(true);
                                    }
                                }
                            }}>
                                <div className="i-landing-tool__content">
                                    {!curUploadBgImgFile &&
                                        <div
                                            className="i-landing-tool-info-background__item-content"
                                        >
                                            <Plus style={{width: '20px', height: '18px', color: '#222529'}}/>
                                            <input
                                                // @ts-ignore
                                                ref={bgImgFileRef}
                                                type="file"
                                                className="hidden"
                                                onChange={uploadImgFile}
                                                accept="image/png, image/jpeg, image/webp"
                                            />
                                        </div>
                                    }
                                    {curUploadBgImgFile &&
                                        <div
                                            className="i-landing-tool-info-background__item-content"
                                            style={{
                                                backgroundImage: `url('${curUploadBgImgFile}')`,
                                                backgroundSize: 'cover'
                                            }}
                                        >
                                            {isUseCurUploadBgImg &&
                                                <Trash2 style={{
                                                    width: '20px',
                                                    height: '18px',
                                                    color: '#D25C4AFF',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '12px',
                                                    border: '1px solid #D25C4AFF'
                                                }}/>
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                            {DemoBgImageList.map((img) => (
                                <div className="i-landing-tool-content" onClick={() => setImgBg2Img(img)}>
                                    <div className="i-landing-tool__content">
                                        <div
                                            className="i-landing-tool-info-background__item-content"
                                            style={{backgroundImage: `url('${img}')`, backgroundSize: 'cover'}}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                    <div className="i-landing-tool-business-button">
                        <Tooltip placement="bottom" title="Up to 5000 x 5000 px">
                            <div className="vip-opt-button i-opt-button">
                                Download HD
                            </div>
                        </Tooltip>

                        <Tooltip placement="bottom" title="Up to original size">
                            <div className="ordinary-opt-button i-opt-button" onClick={downloadImage}>
                                Download Free
                            </div>
                        </Tooltip>
                    </div>

                    <div className="absolute top-0 right-[6px]" onClick={closeEditorTable}>
                        <button className="i-close-btn">
                            <X style={{width: '20px', height: '18px'}} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
